/**
 * Catches render errors of a page so the shell stays usable (React itself
 * logs the error to the console). A failed lazy chunk (typically right after a
 * deployment) gets a "reload" action.
 */
import { RefreshCw } from 'lucide-react';
import { Component, type ReactNode } from 'react';
import { Button } from './Button.js';
import { EmptyState, ErrorState } from './States.js';

function isChunkLoadError(err: unknown): boolean {
  return err instanceof Error && /dynamically imported module|module script failed|Loading chunk|error loading dynamically/i.test(err.message);
}

interface State {
  error: unknown;
}

interface Props {
  children: ReactNode;
  /** A change (e.g. the route path) clears a caught error, without remounting the children. */
  resetKey?: unknown;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  componentDidUpdate(prev: Props): void {
    if (prev.resetKey !== this.props.resetKey && this.state.error !== null) this.setState({ error: null });
  }

  render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    if (isChunkLoadError(error)) {
      return (
        <EmptyState
          icon={<RefreshCw />}
          title="Er is een nieuwe versie"
          action={
            <Button variant="primary" onClick={() => window.location.reload()}>
              Pagina vernieuwen
            </Button>
          }
        >
          TransectLibre is bijgewerkt. Vernieuw de pagina om verder te gaan.
        </EmptyState>
      );
    }
    return <ErrorState title="Deze pagina kon niet worden weergegeven" error={error} onRetry={() => this.setState({ error: null })} />;
  }
}
